import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Drawer,
  Button,
  Message,
  Input,
  Empty,
  Spin,
  Upload,
} from '@bedrock/components';
import type {
  UploadFile,
  UploadChangeParam,
} from '@bedrock/components/lib/Upload/types';
import type { UploadRequestOption } from '@bedrock/components/lib/Upload/Uploader/types';
import {
  Upload as UploadIcon,
  Search,
  FolderOpenFilled,
} from '@bedrock/icons-react';
import NoData from '@bedrock/components/es/Empty/images/large/NoData';
import * as tus from 'tus-js-client';
import { apiService } from '#services';
import type { MuseAssetVo } from '#services/api';
import { useMemoizedFn } from 'ahooks';
import './MuseUploadDrawer.less';
import { EllipsisTooltip } from '@mpc/components';

export interface MuseUploadDrawerProps {
  visible: boolean;
  onClose: () => void;
  view?: boolean;
  demandId: number;
  indexId: number;
  standardCode1: string;
  standardCode2: string;
}

const assetToUploadFile = (asset: MuseAssetVo): UploadFile => ({
  uid: asset.asset_id || String(Math.random()),
  name: asset.file_name || '未知文件',
  size: asset.file_size,
  url: asset.preview_url,
  status: 'done' as const,
  type: asset.file_type,
});

const MuseUploadDrawer: React.FC<MuseUploadDrawerProps> = ({
  visible,
  onClose,
  view = false,
  demandId,
  indexId,
  standardCode1,
  standardCode2,
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploadUrl, setUploadUrl] = useState('');
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadDisabled, setUploadDisabled] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [folderName, setFolderName] = useState('');
  const tusUploadRef = useRef<tus.Upload | null>(null);
  const [uploading, setUploading] = useState(false);
  const triggerRef = useRef<any>(null);
  // 串行发布队列：保证 publish 接口顺序调用，避免服务端并发冲突
  const publishQueueRef = useRef<Promise<void>>(Promise.resolve());

  const fetchAssetList = useMemoizedFn(async () => {
    try {
      const res =
        await apiService.outsourceV2MuseAssetListByChildDemandIdAndIndexIdAndStandardCatalogL1CodeAndStandardCatalogL2Code(
          {
            child_demand_id: demandId,
            index_id: indexId,
            standard_catalog_l1_code: standardCode1,
            standard_catalog_l2_code: standardCode2,
          }
        );
      const assets: MuseAssetVo[] = (res?.data as any) || [];
      const doneFiles = assets.map(assetToUploadFile);
      setFileList((prev) => {
        // 保留正在上传中和失败的文件，替换 done 的文件
        const uploadingFiles = prev.filter(
          (f) => f.status === 'uploading' || f.status === 'error'
        );
        return [...doneFiles, ...uploadingFiles];
      });
    } catch (err) {
      console.log('fetchAssetList error', err);
      Message.error('获取素材列表失败');
    }
  });

  const fetchUploadCredentials = useMemoizedFn(async () => {
    try {
      const res =
        await apiService.outsourceV2MuseUploadUrlByDemandIdAndCooperateIndexAndStandardCode1AndStandardCode2(
          {
            demand_id: demandId,
            cooperate_index: indexId,
            standard_code1: standardCode1,
            standard_code2: standardCode2,
          }
        );
      const data = res?.data as any;
      setUploadUrl(data?.upload_url || '');
      setToken(data?.token || '');
      setFolderName(data?.folder_name || '');
      setUploadDisabled(false);
    } catch (err) {
      console.log('fetchUploadCredentials error', err);
      Message.error('获取上传凭证失败');
      setUploadDisabled(true);
    }
  });

  useEffect(() => {
    if (visible) {
      setLoading(true);
      setSearchKeyword('');
      const promises: Promise<void>[] = [fetchAssetList()];
      promises.push(fetchUploadCredentials());
      Promise.all(promises).finally(() => setLoading(false));
    } else {
      // 关闭抽屉时重置状态
      setFileList([]);
      setUploadUrl('');
      setToken('');
      setSearchKeyword('');
      setFolderName('');
    }
  }, [visible]);

  const customRequest = (option: UploadRequestOption) => {
    const file = option.file as File;
    const upload = new tus.Upload(file, {
      endpoint: uploadUrl,
      headers: { Authorization: token },
      chunkSize: 5 * 1024 * 1024,
      retryDelays: [0, 3000, 5000],
      metadata: {
        filename: file.name,
        filetype: file.type,
      },
      onProgress: (bytesSent, bytesTotal) => {
        const percent = Math.round((bytesSent / bytesTotal) * 100);
        option.onProgress?.({ percent } as any);
      },
      onSuccess: () => {
        const fileKey = upload.url;
        // tus 上传成功后，通过串行队列调用发布接口，避免服务端并发冲突
        // 注意：每个任务末尾必须 catch 兜底，确保队列 Promise 始终 resolved，
        // 避免单个 publish 失败导致后续所有任务无法执行
        publishQueueRef.current = publishQueueRef.current.then(() =>
          apiService
            .outsourceV2MuseResourcePublish({
              demand_id: demandId,
              cooperate_index: indexId,
              standard_code1: standardCode1,
              standard_code2: standardCode2,
              attachment_task_list: [
                {
                  file_key: fileKey,
                  file_name: file.name,
                  file_size: file.size,
                  content_type: file.type,
                },
              ],
            })
            .then(() => {
              option.onSuccess?.({}, undefined);
              setUploading(false);
              fetchAssetList();
            })
            .catch(() => {
              // Message.error('资源发布失败');
              option.onError?.(new Error('资源发布失败') as any);
              setUploading(false);
            })
        ).catch(() => {
          // 兜底：防止队列 Promise 进入 rejected 状态，保证后续任务正常入队执行
        });
      },
      onError: (err) => {
        console.log('onError', err);
        option.onError?.(err as any);
        setUploading(false);
      },
    });
    tusUploadRef.current = upload;
    setUploading(true);
    upload.start();
  };

  const handleChange = useMemoizedFn((info: UploadChangeParam) => {
    const { file } = info;

    if (file && file.status === 'removed') {
      // 查找被删除文件在 fileList 中的原始状态
      const originalFile = fileList.find((f) => f.uid === file.uid);
      if (originalFile?.status === 'done' && originalFile.uid) {
        // 已上传到 Muse 的素材 → 调用删除 API
        apiService
          .outsourceV2MuseAssetByDemandIdAndAttachmentId({
            demand_id: demandId,
            attachment_id: originalFile.uid,
          })
          .then(() => {
            setFileList(info.fileList);
          })
          .catch(() => {
            Message.error('删除失败');
            // 不移除文件
          });
        return;
      } else if (originalFile?.status === 'uploading') {
        // 正在上传 → abort tus 上传
        tusUploadRef.current?.abort();
        setUploading(false);
      }
      // error 状态直接移除
    }

    setFileList(info.fileList);
  });

  const handleClose = useMemoizedFn(() => {
    if (uploading) {
      Message.warn('文件上传中，请稍后');
      return;
    }
    onClose();
  });

  const filteredFileList = useMemo(() => {
    if (!searchKeyword) return fileList;
    return fileList.filter((f) =>
      (f.name || '').toLowerCase().includes(searchKeyword.toLowerCase())
    );
  }, [fileList, searchKeyword]);

  const renderFileInfo = useMemoizedFn(
    ([textNode, pendingNode, sizeNode, failNode, controlNode]) => {
      let fileName = textNode.props.text as string;
      return (
        <div className="flex items-center w-full">
          <EllipsisTooltip text={fileName} keyword={searchKeyword} clamp={1} />
          {pendingNode}
          {failNode}
          {controlNode}
        </div>
      );
    }
  );

  return (
    <Drawer
      visible={visible}
      onClose={handleClose}
      title="上传素材资源"
      width={640}
      footer={null}
      maskClosable={!uploading}
      className="muse-upload-drawer"
      distance={0}
      bodyScrolled
    >
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Spin />
        </div>
      ) : (
        <div className="flex flex-col px-[20px]">
          <div className="sticky top-0 flex flex-col gap-4 z-10 bg-white pb-4">
            {/* 文件夹信息 */}
            {folderName && (
              <div className="flex items-center gap-2">
                <FolderOpenFilled
                  className="text-[20px]"
                  style={{ color: '#2F8FFF' }}
                />
                <span
                  className=" font-semibold text-color-1 truncate"
                  style={{ fontSize: 14 }}
                >
                  {folderName}
                </span>
              </div>
            )}

            {/* 操作栏：搜索 + 上传按钮 */}
            <div className="flex items-center justify-between gap-4 ">
              <Input
                prefix={<Search />}
                placeholder="搜索"
                value={searchKeyword}
                onChange={(val) => {
                  setSearchKeyword(val?.target?.value || '');
                }}
                style={{ width: 280 }}
                allowClear
              />
              {!view && (
                <Button
                  type="primary"
                  icon={<UploadIcon />}
                  disabled={uploadDisabled || !uploadUrl}
                  onClick={() => {
                    triggerRef.current?.click?.();
                  }}
                >
                  上传文件
                </Button>
              )}
            </div>
          </div>

          {/* Upload 组件 + 文件列表 */}
          <div>
            {!view ? (
              <Upload
                className="muse-upload-drawer-upload"
                fileList={filteredFileList}
                onChange={handleChange}
                customRequest={customRequest}
                multiple
                showFileList={true}
                showRemoveIcon={true}
                showProgress={true}
                showDownloadIcon={false}
                renderFileInfo={renderFileInfo}
                // 隐藏默认触发器，使用上面的自定义按钮
                trigger={<span ref={triggerRef} style={{ display: 'none' }} />}
              />
            ) : (
              // 只读模式：仅展示文件列表
              <>
                {filteredFileList.length > 0 ? (
                  <Upload
                    className="muse-upload-drawer-upload"
                    fileList={filteredFileList}
                    showFileList={true}
                    showRemoveIcon={false}
                    showDownloadIcon={false}
                    disabled
                    renderFileInfo={renderFileInfo}
                    trigger={<span style={{ display: 'none' }} />}
                  />
                ) : (
                  <div
                    className="flex justify-center"
                    style={{ padding: '80px 16px' }}
                  >
                    <Empty
                      size="large"
                      imageNode={<NoData />}
                      description={
                        searchKeyword ? '未找到匹配的资源' : '暂无素材'
                      }
                    />
                  </div>
                )}
              </>
            )}
            {!view && filteredFileList.length === 0 && !loading && (
              <div
                className="flex justify-center"
                style={{ padding: '80px 16px' }}
              >
                <Empty
                  size="large"
                  imageNode={<NoData />}
                  description={searchKeyword ? '未找到匹配的资源' : '暂无素材'}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </Drawer>
  );
};

export default MuseUploadDrawer;
